/**
 * Formatters shared by the three sizers.
 *
 * Units, per the backend contract (pricing-scenario-sizers.md §8): rates are
 * DECIMALS (0.085 = 8.5%), prices are bond-point multiples (1.07035),
 * `entityPercentOwnership` and every leverage metric are FRACTIONS. Display
 * formatting is the FE's job; the math is not.
 *
 * `SizerScreen/sizerFormat.js` re-exports this module so the older imports keep
 * working — this file is the single source.
 */

export function fmtMoney(v, opts = {}) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return opts.dash ?? "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
}

export function fmtPct(v, places = 2) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return `${(v * 100).toFixed(places)}%`;
}

// Rates carry 3 decimals — a 12.5bps ladder step is invisible at 2.
export function fmtRate(v) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return `${(v * 100).toFixed(3)}%`;
}

/**
 * A COUPON, at four decimals.
 *
 * `fmtRate` prints three, which is right for a rate-sheet rung. A coupon is the
 * number the deal is quoted at and the one a lock is confirmed against, and the
 * additive RTL build-up (base + term + class + amount + leverage + spread)
 * routinely lands on a fourth decimal — printing three rounds the answer away
 * from the sum of the parts shown directly above it.
 */
export function fmtRateDec(v) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return `${(v * 100).toFixed(4)}%`;
}

// Basis-point adjustments, signed so an add-on reads as one. Still used by any
// bps-scaled surface (e.g. the coupon ladder rungs); the RTL rate block is NOT
// bps anymore — see fmtAdjPct.
export function fmtBps(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Math.round(Number(v));
  return `${n > 0 ? "+" : ""}${n} bps`;
}

// RTL rate-block adjustments (termAdjPct / classAdjPct / loanAmountUptickPct /
// leverageAdjPct). These come back as a PERCENT verbatim — bps retired from RTL
// pricing 2026-08-19 (rateUnits: "percent") — so render as-is, no scaling,
// signed so an add-on reads as one.
export function fmtAdjPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${n.toFixed(3)}%`;
}

// Bond-point price (1.07035). Not a percentage — never multiply it by 100.
export function fmtPrice(v, places = 4) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return Number(v).toFixed(places);
}

export function fmtRatio(v, places = 2) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(places)}x`;
}

export function humanize(s) {
  return String(s)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

/**
 * Provenance line for a `ResolvedInput` ({value, source, reliability,
 * validated}). `validated` is only true at the VERIFIED precedence — an entered
 * or sizer-estimated value still prices, but keeps the loan out of lock-ready,
 * so the panel says which one it got.
 */
export function resolvedSource(ri) {
  if (!ri || ri.source == null) return "not resolved";
  const src = humanize(ri.source).toLowerCase();
  return ri.validated ? `${src} · verified` : `${src} · unverified`;
}

/**
 * Class score FLOORS, read out of `classExplanation`.
 *
 * The floors are versioned policy data — `ClassificationConfig.classThresholds`
 * ({'A+': 7, 'A': 5, 'B': 2, 'C': 0}) — and `/pricing/scenario/max_leverage`
 * quotes them only inside the explanation sentence, as a Python dict repr:
 *
 *   "… total 8 vs class floors {'A+': 7, 'A': 5, 'B': 2, 'C': 0}."
 *   "… vs class thresholds {'A+': 4, 'A': 2, 'B': 1} (B also requires FICO 700+)"  ← GUC
 *
 * So this parses the response's own numbers rather than restating a policy table
 * in the frontend — nothing here is hardcoded, and an unrecognised sentence
 * returns [] so the caller can render without the floor markers.
 *
 * WORTH FIXING PROPERLY: the endpoint should return `classThresholds` (and the
 * max attainable score) as a structured field. Parsing prose is the fallback.
 *
 * @returns {Array<{borrowerClass: string, floor: number}>} highest floor first.
 */
export function parseClassFloors(explanation) {
  if (typeof explanation !== "string") return [];
  const block = explanation.match(/class (?:floors|thresholds)\s*(\{[^}]*\})/i);
  if (!block) return [];
  const out = [];
  const pair = /'([^']+)'\s*:\s*(-?\d+(?:\.\d+)?)/g;
  let m;
  while ((m = pair.exec(block[1])) !== null) {
    out.push({ borrowerClass: m[1], floor: Number(m[2]) });
  }
  return out.sort((a, b) => b.floor - a.floor);
}

/**
 * `YYYY-MM-DD` for a day `n` days from today (local calendar), for the demo
 * scenarios' purchase dates — a fixed date ages into the past, and a purchase
 * dated before today on a non-delayed purchase describes a deal that can't be.
 */
export function isoDateFromToday(n = 0) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
